import React from 'react';

import { classnames } from '@lumapps/classnames';
import { platformConnectorsRoute } from '@lumapps/connectors/routes';
import {
    CLASSNAME as SIDE_NAVIGATION_ITEM_CLASSNAME,
    SideNavigationItem,
    type SideNavigationItemProps,
} from '@lumapps/lumx-back-office/components/SideNavigation';
import { mdiPuzzle } from '@lumapps/lumx/icons';
import { Emphasis, SideNavigationItem as BaseSideNavigationItem } from '@lumapps/lumx/react';
import { marketplaceLibraryRoute } from '@lumapps/marketplace-library/routes';
import { useTranslate, GLOBAL } from '@lumapps/translations';

import { useOpenDeveloperPortal } from '../../hooks/useOpenDeveloperPortal';
import { MARKETPLACE } from '../../keys';
import { platformExtensionsRoute } from '../../routes';

import './index.scss';

export interface PlatformExtensionsSideNavigationProps extends Partial<SideNavigationItemProps> {
    isAllowed: boolean;
}

const PlatformExtensionsSideNavigation = ({ isAllowed, ...restOfProps }: PlatformExtensionsSideNavigationProps) => {
    const { translateKey } = useTranslate();
    const { openDeveloperPortal, isLoading } = useOpenDeveloperPortal();

    if (!isAllowed) {
        return null;
    }

    return (
        <SideNavigationItem
            {...restOfProps}
            label={translateKey(MARKETPLACE.PLATFORM_EXTENSIONS)}
            icon={mdiPuzzle}
            id="mp"
        >
            <SideNavigationItem label={translateKey(GLOBAL.LIBRARY)} route={marketplaceLibraryRoute} />
            <SideNavigationItem
                label={translateKey(MARKETPLACE.INSTALLED_EXTENSIONS)}
                route={platformExtensionsRoute}
            />
            <SideNavigationItem
                label={translateKey(GLOBAL.CONNECTORS)}
                id="connectors"
                route={platformConnectorsRoute}
            />
            {/*
             * The Developer Portal entry is an authenticated action, not a navigation link:
             * on click it fetches a one-time login URL and opens the dev-portal already
             * authenticated (see useOpenDeveloperPortal). The back-office SideNavigationItem
             * wrapper drives everything through the router and drops a consumer `onClick`, so we
             * render the underlying LumX SideNavigationItem directly as a button (no `route`/`href`).
             * Trade-off: this entry is not indexed in the SideNavigation Spotlight search.
             */}
            <BaseSideNavigationItem
                className={classnames(SIDE_NAVIGATION_ITEM_CLASSNAME, {
                    [`${SIDE_NAVIGATION_ITEM_CLASSNAME}--is-loading`]: isLoading,
                })}
                emphasis={Emphasis.medium}
                label={translateKey(MARKETPLACE.DEVELOPER_PORTAL)}
                id="developer-portal"
                toggleButtonProps={{ label: translateKey(GLOBAL.TOGGLE) }}
                // Duplicate opens while a fetch is in flight are guarded inside the hook
                // (synchronous ref); the `--is-loading` modifier + `aria-disabled` here only
                // surface the in-flight state to the user, since `SideNavigationItemProps.linkProps`
                // is typed as plain AnchorHTMLAttributes (no native `disabled`/`isDisabled` prop).
                linkProps={{ 'aria-disabled': isLoading }}
                onClick={openDeveloperPortal}
            />
        </SideNavigationItem>
    );
};

PlatformExtensionsSideNavigation.displayName = 'SideNavigationItem';

export { PlatformExtensionsSideNavigation };
